import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import AtlassianAuth from './AtlassianAuth.js';
import MiaPlatformAuth from './MiaPlatformAuth.js';
import GitHubAuth from './GitHubAuth.js';
import miaFlowAuth from './MiaFlowAuth.js';
import genericMcpAuth from './GenericMcpAuth.js';
import analyticsService from '../services/analyticsService';

/**
 * OAuth Callback Component
 * Handles the OAuth redirect and token exchange for multiple providers:
 * - Mia Flow app authentication (login to app)
 * - MCP Server authentication (connector authentication)
 */
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState('unknown');

  useEffect(() => {
    // Prevent double execution in React StrictMode
    let ignore = false;
    
    const handleCallback = async () => {
      if (ignore) return;
      
      try {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');

        // Determine which provider based on URL path
        const pathname = location.pathname;
        let currentProvider = 'atlassian'; // default
        let serverId = null;
        
        // Check provider from path
        if (pathname.includes('/auth/github/callback')) {
          currentProvider = 'github';
        } else if (pathname.includes('/auth/mia-platform/callback')) {
          // Check if state indicates this is app login or connector auth
          const storedAppState = sessionStorage.getItem('mia_flow_app_auth_state');
          const storedMcpState = sessionStorage.getItem('mia_platform_auth_state');
          
          if (state === storedAppState) {
            currentProvider = 'mia-flow-app';
          } else if (state === storedMcpState) {
            currentProvider = 'mia-platform-mcp';
          } else {
            // Default to app login for new auth flow
            currentProvider = 'mia-flow-app';
          }
        } else if (pathname.includes('/auth/generic-mcp/')) {
          const match = pathname.match(/\/auth\/generic-mcp\/([^/]+)\/callback/);
          if (match) {
            serverId = match[1];
            currentProvider = 'generic-mcp';
          }
        }
        
        setProvider(currentProvider);

        // eslint-disable-next-line no-console
        console.log('🔄 [FE] AuthCallback received:', { 
          provider: currentProvider,
          serverId,
          path: pathname,
          has_code: !!code, 
          has_state: !!state, 
          error: errorParam
        });

        if (errorParam) {
          // eslint-disable-next-line no-console
          console.error('❌ [FE] OAuth error in callback:', errorParam);
          setError(`OAuth error: ${errorParam}`);
          setStatus('error');
          return;
        }

        if (!code) {
          // eslint-disable-next-line no-console
          console.error('❌ [FE] Missing authorization code in callback');
          setError('Missing authorization code');
          setStatus('error');
          return;
        }

        // Exchange code for token based on provider
        // eslint-disable-next-line no-console
        console.log(`🔄 [FE] Starting ${currentProvider} token exchange...`);
        
        let result;
        if (currentProvider === 'mia-flow-app') {
          // Mia Flow app login
          result = await miaFlowAuth.handleCallback();
          
          // eslint-disable-next-line no-console
          console.log('🔄 [FE] Mia Flow handleCallback result:', result);
          
          if (result.success) {
            // eslint-disable-next-line no-console
            console.log('✅ [FE] Mia Flow app login successful');
            
            // Track successful login
            analyticsService.trackAuth('login_completed', {
              method: 'oauth',
              provider: 'mia-platform'
            });
            
            setStatus('success');
            
            // Redirect to home page immediately
            const redirectTo = sessionStorage.getItem('auth_redirect_to') || '/';
            sessionStorage.removeItem('auth_redirect_to');
            
            // eslint-disable-next-line no-console
            console.log('🔄 [FE] Redirecting to:', redirectTo);

            // Use immediate redirect
            window.location.href = redirectTo;
          } else if (result.shouldRetry) {
            // PKCE state expired, redirect to login
            // eslint-disable-next-line no-console
            console.log('⚠️ [FE] Auth state expired, redirecting to login...');
            setError('Authentication state expired. Please login again.');
            setStatus('error');
            setTimeout(() => {
              navigate('/login', {replace: true});
            }, 2000);
            return;
          } else {
            throw new Error(result.error || 'Authentication failed');
          }
        } else if (currentProvider === 'github') {
          // GitHub connector authentication
          // Exchange code for token
          const tokenData = await GitHubAuth.exchangeCodeForToken(code, state);
          
          // Send to backend
          try {
            await GitHubAuth.sendCredentialsToBackend(tokenData);
          } catch (backendError) {
            // eslint-disable-next-line no-console
            console.error('Failed to save to backend:', backendError);
          }
          
          // eslint-disable-next-line no-console
          console.log('✅ [FE] GitHub OAuth successful');
          setStatus('success');
          
          // Notify opener if in popup
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({ type: 'github_auth_success' }, window.location.origin);
            setTimeout(() => window.close(), 500);
            return;
          }
          
          // Otherwise redirect to tools
          setTimeout(() => {
            navigate('/tools', {replace: true});
          }, 1000);
          return;
        } else if (currentProvider === 'mia-platform-mcp') {
          // MCP Server connector authentication
          result = await MiaPlatformAuth.exchangeCodeForToken(code, state);
        } else if (currentProvider === 'generic-mcp' && serverId) {
          result = await genericMcpAuth.exchangeCodeForTokens(serverId, code, state);
          
          if (window.opener) {
            window.opener.postMessage({
              type: `generic-mcp-${serverId}-auth-success`,
              code,
              state
            }, window.location.origin);
            
            setTimeout(() => {
              window.close();
            }, 1000);
            
            return;
          }
        } else {
          // Atlassian and other OAuth providers go through the provider link service
          // No need for explicit token exchange
          // eslint-disable-next-line no-console
          console.log('⚠️ Unknown provider or centrally-managed OAuth:', currentProvider);
          setStatus('success');
          setTimeout(() => {
            navigate('/', {replace: true});
          }, 1000);
          return;
        }
        
        // eslint-disable-next-line no-console
        console.log(`✅ [FE] ${currentProvider} token exchange successful:`, {
          has_result: !!result
        });
        
        setStatus('success');
        
        // Redirect based on provider
        if (currentProvider === 'mia-platform-mcp') {
          // MCP connector auth - stay on tools page
          // eslint-disable-next-line no-console
          console.log('🔄 [FE] Redirecting to tools page...');
          setTimeout(() => {
            navigate('/tools', {replace: true});
          }, 1500);
        } else if (currentProvider !== 'mia-flow-app') {
          // Other providers - redirect to home
          // eslint-disable-next-line no-console
          console.log('🔄 [FE] Redirecting to home page...');
          setTimeout(() => {
            navigate('/', {replace: true});
          }, 1500);
        }
        // Note: mia-flow-app redirects immediately in its handler above

      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('❌ AuthCallback error:', err);
        setError(err.message);
        setStatus('error');
        
        // Notify parent window about error if in popup
        if (window.opener && provider === 'generic-mcp') {
          const serverId = location.pathname.match(/\/auth\/generic-mcp\/([^/]+)\/callback/)?.[1];
          if (serverId) {
            window.opener.postMessage({
              type: `generic-mcp-${serverId}-auth-error`,
              error: err.message
            }, window.location.origin);
          }
        }
      }
    };

    handleCallback();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      ignore = true;
    };
  }, [location, navigate, provider]);

  const getProviderDisplayName = () => {
    switch (provider) {
      case 'mia-flow-app':
        return 'Mia Flow';
      case 'mia-platform-mcp':
        return 'Mia Platform MCP';
      case 'atlassian':
        return 'Atlassian';
      case 'generic-mcp':
        return 'MCP Server';
      default:
        return 'OAuth';
    }
  };

  return (
    <div className="auth-callback">
      <div className="auth-callback-content">
        {status === 'processing' && (
          <div className="auth-status">
            <div className="spinner"></div>
            <h2>{`Completing ${getProviderDisplayName()} authentication...`}</h2>
            <p>{'Please wait while we verify your credentials.'}</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="auth-status success">
            <h2>{`✅ ${getProviderDisplayName()} authentication successful!`}</h2>
            <p>{'You will be redirected shortly.'}</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="auth-status error">
            <h2>{`❌ ${getProviderDisplayName()} authentication failed`}</h2>
            <p>{error}</p>
            <button 
              className="back-button"
              onClick={() => navigate('/', {replace: true})}
              type="button"
            >
              {'Return to App'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
